import { BaseController } from 'controllers/BaseController'
import { newProjectActions } from 'features/BillOfMaterials/store/projectReducer'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import { BomItemCheckListItem } from 'services/APIs/InternalAPI/internal-api.contracts'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import store from 'store/configureStore'
import { GetContext } from 'store/storeUtils'

interface UpdateBomItemCheckListItemRequest {
  done?: boolean
  customDescription?: string
}

interface IBomItemCheckListAPI {
  GetBomItemCheckList: (
    bomItemId: string,
  ) => Promise<Record<string, BomItemCheckListItem[]>>
  AddBomItemCheckListItem: (
    bomItemId: string,
    item: BomItemCheckListItem,
  ) => Promise<string>
  UpdateBomItemCheckListItem: (
    bomItemId: string,
    itemId: string,
    req: UpdateBomItemCheckListItemRequest,
  ) => Promise<void>
  DeleteBomItemCheckListItem: (
    bomItemId: string,
    itemId: string,
  ) => Promise<void>
}

export const CheckListAPIIds = (bomItemId: string) => ({
  GetBomItemCheckList: `GetBomItemCheckList-${bomItemId}`,
  AddBomItemCheckListItem: `AddBomItemCheckListItem-${bomItemId}`,
  UpdateBomItemCheckListItem: (itemId: string) =>
    `UpdateBomItemCheckListItem-${bomItemId}-${itemId}`,
  DeleteBomItemCheckListItem: (itemId: string) =>
    `DeleteBomItemCheckListItem-${bomItemId}-${itemId}`,
})

class BomItemCheckListAPI extends InternalAPI implements IBomItemCheckListAPI {
  constructor(
    partyId: string,
    projectId: string,
    onRequestChange: OnRequestChangeCallback,
  ) {
    super(
      `/api/parties/${partyId}/projects/${projectId}/bomItem`,
      onRequestChange,
    )
  }

  async GetBomItemCheckList(
    bomItemId: string,
  ): Promise<Record<string, BomItemCheckListItem[]>> {
    return this.GetAsync<Record<string, BomItemCheckListItem[]>>({
      id: CheckListAPIIds(bomItemId).GetBomItemCheckList,
      relativePath: `/${bomItemId}/checkList`,
    })
  }

  async AddBomItemCheckListItem(
    bomItemId: string,
    item: BomItemCheckListItem,
  ): Promise<string> {
    return this.PostAsync<string>({
      id: CheckListAPIIds(bomItemId).AddBomItemCheckListItem,
      relativePath: `/${bomItemId}/checkList`,
      data: item,
    })
  }

  async UpdateBomItemCheckListItem(
    bomItemId: string,
    itemId: string,
    req: UpdateBomItemCheckListItemRequest,
  ): Promise<void> {
    const reqId = CheckListAPIIds(bomItemId).UpdateBomItemCheckListItem(itemId)

    return this.PutAsync({
      id: reqId,
      relativePath: `/${bomItemId}/checkList/${itemId}`,
      data: req,
    })
  }

  async DeleteBomItemCheckListItem(
    bomItemId: string,
    itemId: string,
  ): Promise<void> {
    return this.DeleteAsync({
      id: CheckListAPIIds(bomItemId).DeleteBomItemCheckListItem(itemId),
      relativePath: `/${bomItemId}/checkList/${itemId}`,
    })
  }
}

export class DrawingCheckListController extends BaseController<IBomItemCheckListAPI> {
  constructor() {
    super((onRequestChange) => {
      const { partyId, projectId } = GetContext()
      return new BomItemCheckListAPI(partyId, projectId, onRequestChange)
    })
  }

  private updateStore(
    bomItemPointer: BomItemPointer,
    checkList: Record<string, BomItemCheckListItem[]>,
    fullReplace?: boolean,
  ) {
    const activeCheckListFeatureId =
      store.getState().project.currentCheckList?.activeFeatureId

    store.dispatch(
      newProjectActions.setProjectStateProperties({
        currentCheckList: {
          bomItemPointer: bomItemPointer,
          checkList: checkList,
          activeFeatureId: activeCheckListFeatureId,
        },
        mergeStrategy: fullReplace ? 'fullReplace' : 'keepLatestSource',
      }),
    )
  }

  async GetBomItemCheckList(
    bomItemPointer: BomItemPointer,
  ): Promise<Record<string, BomItemCheckListItem[]>> {
    try {
      const result = await this.api.GetBomItemCheckList(bomItemPointer.id)

      this.updateStore(bomItemPointer, result, true)

      return result
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  async AddBomItemCheckListItem(
    bomItemPointer: BomItemPointer,
    item: BomItemCheckListItem,
  ): Promise<string> {
    try {
      const itemId = await this.api.AddBomItemCheckListItem(
        bomItemPointer.id,
        item,
      )

      return itemId
    } catch (error) {
      throw this.HandleError(error)
    } finally {
      this.GetBomItemCheckList(bomItemPointer)
    }
  }

  async UpdateBomItemCheckListItem(
    bomItemPointer: BomItemPointer,
    itemId: string,
    itemCategory: string,
    req: UpdateBomItemCheckListItemRequest,
  ): Promise<void> {
    try {
      this.updateStore(bomItemPointer, {
        [itemCategory.toLowerCase()]: [
          ...(
            store.getState().project.currentCheckList?.checkList?.[
              itemCategory.toLowerCase()
            ] || []
          ).map((item) => {
            if (item.itemId === itemId) {
              return { ...item, ...req }
            }
            return item
          }),
        ],
      })

      await this.api.UpdateBomItemCheckListItem(bomItemPointer.id, itemId, req)
    } catch (error) {
      this.GetBomItemCheckList(bomItemPointer)
      throw this.HandleError(error)
    }
  }

  async DeleteBomItemCheckListItem(
    bomItemPointer: BomItemPointer,
    itemId: string,
    itemCategory: string,
  ): Promise<void> {
    try {
      await this.api.DeleteBomItemCheckListItem(bomItemPointer.id, itemId)
    } catch (error) {
      throw this.HandleError(error)
    } finally {
      await this.GetBomItemCheckList(bomItemPointer)
    }
  }
}
